import React, { useMemo, useState } from 'react';
import { Key, Trash2, Search, Pencil, UserPlus, Send, CheckCircle2, AlertTriangle, Clock, UploadCloud, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

/**
 * Render an Invited / Activated status pill for a user row.
 *
 * - `invite_token` present  → "Invited" (waiting for the customer to
 *   click the welcome email & set a password). Shows the email-delivery
 *   chip too (✓ sent / ⚠ failed / ⏳ pending).
 * - `invite_token` missing  → "Activated" (account is in active use).
 *
 * Self-onboarded users who never went through admin-invite will fall
 * through to "Activated" since they never had an invite_token.
 */
const InviteStatusChip = ({ user }) => {
  if (user.invite_token) {
    const status = user.email_status || 'pending';
    const meta = {
      sent:    { Icon: CheckCircle2, cls: 'bg-amber-100 text-amber-800', label: 'Invited' },
      failed:  { Icon: AlertTriangle, cls: 'bg-red-100 text-red-800',   label: 'Invite — email failed' },
      pending: { Icon: Clock,        cls: 'bg-amber-50 text-amber-700', label: 'Invited — email queued' },
    }[status] || { Icon: Clock, cls: 'bg-amber-50 text-amber-700', label: 'Invited' };
    const { Icon, cls, label } = meta;
    const attempts = user.email_attempts || 0;
    return (
      <span
        data-testid={`invite-chip-${user.id}`}
        data-email-status={status}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}
        title={
          status === 'failed' && user.email_error
            ? `Email send failed: ${user.email_error}`
            : status === 'sent' && user.email_sent_at
              ? `Last sent ${new Date(user.email_sent_at).toLocaleString()}${attempts > 1 ? ` · ${attempts} attempts` : ''}`
              : 'Welcome email is queued'
        }
      >
        <Icon className="w-3 h-3" />
        {label}
        {attempts > 1 && <span className="ml-0.5 text-[10px] opacity-75">×{attempts}</span>}
      </span>
    );
  }
  return (
    <span
      data-testid={`activated-chip-${user.id}`}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800"
      title="Account is active — user has set a password"
    >
      <CheckCircle2 className="w-3 h-3" />
      Activated
    </span>
  );
};

export const UsersTable = ({
  users,
  currentUserId,
  loading,
  searchTerm,
  onSearchChange,
  onResetPassword,
  onDeleteUser,
  onEditUser,
  onInviteUser,
  onBulkInvite,
  onResendInvite,
  onChangeBillingMode,
}) => {
  // Sort toggles (iter104b) — admin clicks a header to sort ASC, click
  // again for DESC, third click clears (back to default order). Held
  // in component-local state because the parent doesn't care about
  // ordering. Search-filtered list flows into the sort, so the two
  // operations compose cleanly.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  const handleHeaderClick = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    // Third click — clear sort.
    setSortKey(null);
    setSortDir('asc');
  };

  // Stable accessor map. Each entry returns a primitive comparable
  // value so JS's default `<` ordering works. Falsy values get pushed
  // to the end via the sentinel '~~~' (lex-greater than any real
  // alphanumeric content) so missing data doesn't crowd the top.
  const sortAccessors = {
    name: (u) => `${u.last_name || ''} ${u.first_name || ''}`.trim().toLowerCase() || '~~~',
    email: (u) => (u.email || '').toLowerCase() || '~~~',
    pricing: (u) => (u.pricing_tier || '').toLowerCase() || '~~~',
    billing: (u) => (u.billing_mode || '').toLowerCase() || '~~~',
    status: (u) => {
      // Activated < Invited (sent) < Invited (pending) < Invited (failed).
      // Numeric weights so sort is fully deterministic across email_status combos.
      if (!u.invite_token) return 0;
      const map = { sent: 1, pending: 2, failed: 3 };
      return map[u.email_status] || 2;
    },
    role: (u) => (u.is_admin ? 'admin' : (u.is_guest ? 'guest' : 'customer')),
  };

  const filtered = useMemo(() => {
    const matched = users.filter((u) =>
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.last_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (!sortKey || !sortAccessors[sortKey]) return matched;
    const accessor = sortAccessors[sortKey];
    const dir = sortDir === 'desc' ? -1 : 1;
    // Stable sort — JS Array.prototype.sort is stable since ES2019, so
    // equal-key rows preserve their input order.
    return [...matched].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchTerm, sortKey, sortDir]);

  const SortHeader = ({ k, label, align = 'left' }) => {
    const active = sortKey === k;
    const Icon = !active ? ArrowUpDown : (sortDir === 'asc' ? ArrowUp : ArrowDown);
    return (
      <th
        scope="col"
        className={`text-${align} py-3 px-2 font-semibold ${active ? 'text-[#2F5D3A]' : 'text-gray-700'} select-none`}
      >
        <button
          type="button"
          onClick={() => handleHeaderClick(k)}
          className={`inline-flex items-center gap-1 uppercase tracking-wide ${active ? 'text-[#2F5D3A]' : 'text-gray-700 hover:text-[#4A7C59]'}`}
          aria-sort={!active ? 'none' : (sortDir === 'asc' ? 'ascending' : 'descending')}
          data-testid={`users-sort-${k}`}
          title={`Sort by ${label.toLowerCase()}${active ? ` — currently ${sortDir === 'asc' ? 'ascending' : 'descending'}, click to ${sortDir === 'asc' ? 'reverse' : 'clear'}` : ''}`}
        >
          {label}
          <Icon className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
        </button>
      </th>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8" data-testid="admin-users-panel">
      <div className="mb-6 flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6FBF73]"
            data-testid="admin-users-search"
          />
        </div>
        {onBulkInvite && (
          <button
            onClick={onBulkInvite}
            data-testid="admin-bulk-invite-btn"
            className="inline-flex items-center gap-2 bg-white hover:bg-green-50 text-[#4A7C59] border border-[#4A7C59] px-5 py-3 rounded-lg font-semibold shadow-sm transition-colors whitespace-nowrap"
            title="Invite many users at once from a CSV or Excel sheet"
          >
            <UploadCloud className="w-5 h-5" />
            Bulk Upload
          </button>
        )}
        {onInviteUser && (
          <button
            onClick={onInviteUser}
            data-testid="admin-invite-user-btn"
            className="inline-flex items-center gap-2 bg-[#4A7C59] hover:bg-[#3D6A4A] text-white px-5 py-3 rounded-lg font-semibold shadow-sm transition-colors whitespace-nowrap"
          >
            <UserPlus className="w-5 h-5" />
            Add User
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6FBF73] mx-auto"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* Tightened layout — `table-auto` so each column sizes to
               its content, smaller text/padding, and `truncate` on
               name/email/company so long values get an ellipsis (with
               full value on hover via `title=`) instead of forcing
               the table wider than the viewport. */}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-300 text-xs uppercase tracking-wide">
                <SortHeader k="name" label="Name" />
                <SortHeader k="email" label="Email" />
                <th className="text-left py-3 px-2 font-semibold text-gray-700">Company</th>
                <SortHeader k="pricing" label="Pricing" />
                <SortHeader k="billing" label="Billing" />
                <SortHeader k="status" label="Status" />
                <SortHeader k="role" label="Role" />
                <th className="text-right py-3 px-2 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td
                    className="py-3 px-2 text-gray-800 max-w-[140px] truncate"
                    title={`${u.first_name || ''} ${u.last_name || ''}`.trim()}
                  >
                    {u.first_name} {u.last_name}
                  </td>
                  <td
                    className="py-3 px-2 text-gray-800 max-w-[200px] truncate"
                    title={u.email}
                  >
                    {u.email}
                  </td>
                  <td
                    className="py-3 px-2 text-gray-600 max-w-[140px] truncate"
                    title={u.company || 'N/A'}
                  >
                    {u.company || 'N/A'}
                  </td>
                  <td className="py-3 px-2">
                    <span
                      data-testid={`admin-user-tier-${u.id}`}
                      className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                        u.pricing_tier === 'evergreen'
                          ? 'bg-green-100 text-green-800 border border-green-300'
                          : 'bg-blue-100 text-blue-800 border border-blue-300'
                      }`}
                      title={u.pricing_tier === 'evergreen' ? 'Evergreen Strategic Partner — 30% off' : 'Standard retail pricing'}
                    >
                      {u.pricing_tier === 'evergreen' ? 'Evergreen 30%' : 'Standard'}
                    </span>
                  </td>
                  <td className="py-3 px-2">
                    {/* Billing-mode dropdown — flips between Net-30
                        invoicing and Converge self-checkout. Hidden
                        for admins (irrelevant — admins place custom
                        orders via the Custom Orders tab). Compact
                        labels keep the column from forcing horizontal
                        scroll on the User Management table. */}
                    {(!u.is_admin && onChangeBillingMode) ? (
                      <select
                        data-testid={`admin-user-billing-${u.id}`}
                        value={u.billing_mode || 'pay_now'}
                        onChange={(e) => onChangeBillingMode(u, e.target.value)}
                        className={`text-xs font-semibold px-1.5 py-1 rounded-md border focus:ring-2 focus:ring-[#6FBF73] focus:outline-none cursor-pointer ${
                          (u.billing_mode || 'pay_now') === 'invoice'
                            ? 'bg-amber-50 text-amber-800 border-amber-300'
                            : 'bg-gray-50 text-gray-700 border-gray-300'
                        }`}
                      >
                        <option value="pay_now">Pay Now</option>
                        <option value="invoice">Net 30</option>
                      </select>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 px-2">
                    <InviteStatusChip user={u} />
                  </td>
                  <td className="py-3 px-2">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        u.is_admin ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {u.is_admin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-0.5 justify-end">
                      <button
                        onClick={() => onEditUser(u)}
                        className="p-1.5 text-[#4A7C59] hover:bg-green-50 rounded-lg transition-colors"
                        title="Edit User"
                        data-testid={`admin-edit-user-${u.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {u.invite_token && onResendInvite && (
                        <button
                          onClick={() => onResendInvite(u)}
                          className="p-1.5 text-sky-600 hover:bg-sky-50 rounded-lg transition-colors"
                          title="Resend welcome email (refreshes invite link)"
                          data-testid={`admin-resend-invite-${u.id}`}
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onResetPassword(u)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Reset Password"
                        data-testid={`admin-reset-password-${u.id}`}
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      {u.id !== currentUserId && (
                        <button
                          onClick={() => onDeleteUser(u)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete User"
                          data-testid={`admin-delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
